import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Container,
  TextField,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClaimLiteRaveCode, useVerifyLiteRaveSignup } from "@/api";
import { useAuthContext } from "@/context/AuthContext";

export const LiteRaveClaim = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Safely get user from context (might be undefined if unauthenticated)
  const { user } = useAuthContext();

  // Signup State
  const [email, setEmail] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [touched, setTouched] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [verifySignup, signupResult, signupLoading, signupError] =
    useVerifyLiteRaveSignup();

  // Claim State
  const [claimCode, setClaimCode] = useState("");
  const [claimed, setClaimed] = useState(false);
  const [doClaim, claimResult, claimLoading, claimError] =
    useClaimLiteRaveCode();

  // Initialize codes from URL
  useEffect(() => {
    const codeParam = searchParams.get("c");
    if (codeParam) {
      setSignupCode(codeParam);
      setClaimCode(codeParam);
    }
  }, [searchParams]);

  // Email validation
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(emailRegex.test(email));
  }, [email]);

  // Signup Handler
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    if (!emailValid || !signupCode.trim()) {
      return;
    }

    try {
      const returnUrl = `${window.location.origin}/lite-raves/claim`;
      await verifySignup({ email, code: signupCode, returnUrl });
      setSignupSuccess(true);
    } catch (err) {
      console.error("Signup error:", err);
    }
  };

  // Claim Handler
  const handleClaim = async () => {
    if (!claimCode) {
      return;
    }

    try {
      await doClaim({ code: claimCode });
      setClaimed(true);
    } catch (err) {
      console.error("Claim error:", err);
    }
  };

  // ----------------------------------------------------------------------
  // RENDER: Authenticated (Claim Flow)
  // ----------------------------------------------------------------------
  if (user) {
    if (claimed && claimResult?.status === "success") {
      return (
        <Container maxWidth="sm">
          <Box sx={{ mt: 8, mb: 8 }}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 3, sm: 6 } }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    fontWeight="bold"
                  >
                    🎉 Success!
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    You're all set!
                  </Typography>
                </Box>

                <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                  Your redemption code has been claimed successfully!
                </Alert>

                <Typography variant="body1" sx={{ mb: 4, textAlign: "center" }}>
                  You can now create and view lite raves.
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/lite-raves/home")}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }

    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, mb: 8 }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              Claim Your Code
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight="normal">
              Activate your account to get started
            </Typography>
          </Box>

          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 3, sm: 6 } }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, textAlign: "center" }}
              >
                Click the button below to claim your redemption code and
                activate your lite rave account.
              </Typography>

              {claimCode && (
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    textAlign: "center",
                    border: "1px dashed",
                    borderColor: "grey.400",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Redemption Code
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "monospace",
                      letterSpacing: 3,
                      fontWeight: "bold",
                    }}
                  >
                    {claimCode}
                  </Typography>
                </Box>
              )}

              {claimError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {claimError}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleClaim}
                disabled={claimLoading || !claimCode}
                sx={{
                  mb: 2,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {claimLoading ? (
                  <CircularProgress size={28} color="inherit" />
                ) : (
                  "Claim Code"
                )}
              </Button>

              {!claimCode && (
                <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                  No redemption code found. Please check your email link.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // ----------------------------------------------------------------------
  // RENDER: Unauthenticated (Signup Flow)
  // ----------------------------------------------------------------------
  if (signupSuccess) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, mb: 8 }}>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 3, sm: 6 } }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  fontWeight="bold"
                >
                  Check Your Email
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  We've sent a magic link to <strong>{email}</strong>
                </Typography>
              </Box>

              <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                Click the link in your email to sign in and access your lite
                rave account.
              </Alert>

              <Typography variant="body2" color="text.secondary" align="center">
                The link will expire in 60 minutes. If you don't see the email,
                check your spam folder.
              </Typography>

              {process.env.NODE_ENV === "development" &&
                signupResult?.devLink && (
                  <Box sx={{ mt: 4 }}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        Development Mode - Magic Link:
                      </Typography>
                      <a
                        href={signupResult.devLink}
                        style={{ wordBreak: "break-all" }}
                      >
                        {signupResult.devLink}
                      </a>
                    </Alert>
                  </Box>
                )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 8 }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            Join Lite Rave
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight="normal">
            Enter your email and redemption code to get started
          </Typography>
        </Box>

        <Card elevation={3} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 3, sm: 6 } }}>
            <form onSubmit={handleSignup}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={touched && !emailValid}
                helperText={
                  touched && !emailValid ? "Please enter a valid email" : ""
                }
                sx={{ mb: 3 }}
                disabled={signupLoading}
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Redemption Code"
                value={signupCode}
                onChange={(e) => setSignupCode(e.target.value.toUpperCase())}
                sx={{ mb: 4 }}
                disabled={signupLoading}
                inputProps={{
                  style: { textTransform: "uppercase", letterSpacing: "1px" },
                }}
                variant="outlined"
              />

              {signupError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {signupError}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={signupLoading || !emailValid || !signupCode.trim()}
                sx={{
                  mb: 3,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {signupLoading ? (
                  <CircularProgress size={28} color="inherit" />
                ) : (
                  "Get Magic Link"
                )}
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                We'll send you a magic link to sign in. No password needed!
              </Typography>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};
